"use client";

import { Switch as SwitchPrimitive } from "@base-ui/react/switch";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/utils/class-name";

const switchVariants = cva(
    "group/switch inline-flex shrink-0 cursor-pointer rounded-full px-px py-px inset-shadow-[0_1px_--theme(--color-black/4%)] transition-all outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-64 data-checked:bg-primary data-unchecked:bg-input",
    {
        variants: {
            size: {
                sm: "h-4 w-7",
                default: "h-[1.125rem] w-7.5",
                lg: "h-6 w-11",
                xl: "h-8 w-14",
            },
        },
        defaultVariants: {
            size: "default",
        },
    },
);

const thumbVariants = cva(
    "pointer-events-none block rounded-full bg-background shadow-sm transition-[translate,width] data-unchecked:translate-x-0",
    {
        variants: {
            size: {
                sm: "size-3 group-active/switch:w-3.5 data-checked:translate-x-3 data-checked:group-active/switch:translate-x-2.5",
                default:
                    "size-4 group-active/switch:w-4.5 data-checked:translate-x-3 data-checked:group-active/switch:translate-x-2.5",
                lg: "size-5 group-active/switch:w-6 data-checked:translate-x-5 data-checked:group-active/switch:translate-x-4",
                xl: "size-7 group-active/switch:w-8 data-checked:translate-x-6 data-checked:group-active/switch:translate-x-5",
            },
        },
        defaultVariants: {
            size: "default",
        },
    },
);

interface SwitchProps extends SwitchPrimitive.Root.Props, VariantProps<typeof switchVariants> {}

function Switch({ className, size, ...props }: SwitchProps) {
    return (
        <SwitchPrimitive.Root data-slot={"switch"} className={cn(switchVariants({ size }), className)} {...props}>
            <SwitchPrimitive.Thumb data-slot={"switch-thumb"} className={thumbVariants({ size })} />
        </SwitchPrimitive.Root>
    );
}

export { Switch, switchVariants, type SwitchProps };
